import { useState, useEffect, useRef } from "react";

export function ChangingNumbers({
  from,
  to,
  duration = 1500,
  decimals = 2,
  divisor = 1,
}: {
  from: number | undefined;
  to: number | undefined;
  duration?: number;
  decimals?: number;
  divisor?: number;
}) {
  const [currentValue, setCurrentValue] = useState<number>();
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const animationRef = useRef<number>();
  const startTimeRef = useRef<number>(0);

  // Function to start the animation (only if it hasn't run before)
  const startAnimation = () => {
    if (from !== undefined && to !== undefined && !hasAnimated) {
      setCurrentValue(from);
      setIsAnimating(true);
    }
  };

  // Manually restart animation (for click handler)
  const restartAnimation = () => {
    if (from !== undefined && to !== undefined) {
      setCurrentValue(from);
      setIsAnimating(true);
      setHasAnimated(false); // Reset the animation state for manual restart
    }
  };

  // Setup Intersection Observer to detect when element enters viewport
  useEffect(() => {
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.1 // trigger when at least 10% of the element is visible
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // When element enters viewport and hasn't animated yet
        if (entry.isIntersecting && !hasAnimated) {
          startAnimation();
        }
      }
    }, options);

    // Start observing the element
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [from, to, hasAnimated]); // Re-initialize when from/to values or hasAnimated changes

  // Handle the animation itself using requestAnimationFrame
  useEffect(() => {
    if (from === undefined || to === undefined || !isAnimating) {
      if (!isAnimating && to !== undefined) {
        setCurrentValue(to);
      }
      return;
    }

    // Clear any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startTimeRef.current = performance.now();
    
    const animate = (timestamp: number) => {
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(1, elapsed / duration);
      
      // Improved easing function for smoother animation
      const easedProgress = easeOutCubic(progress);
      
      const newValue = from + (to - from) * easedProgress;
      setCurrentValue(newValue);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setCurrentValue(to);
        setIsAnimating(false);
        setHasAnimated(true); // Mark that animation has completed
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [from, to, duration, isAnimating]);

  const formatValue = (value: number | undefined) => {
    if (value === undefined) return "";
    
    // First, handle the target value to determine formatting
    const targetValue = to;
    if (targetValue === undefined) return "";
    
    // Calculate the values with division
    const dividedValue = value / divisor;
    const targetDividedValue = targetValue / divisor;
    
    // Format with fixed decimals
    const fixedValue = dividedValue.toFixed(decimals);
    const targetFixed = targetDividedValue.toFixed(decimals);
    
    // Split into integer and decimal parts
    const [integerPart, decimalPart] = fixedValue.split('.');
    const [targetInt] = targetFixed.split('.');
    
    // Find required length for padding
    const targetLength = targetInt.replace(/,/g, '').length;
    
    // Pad the integer part to match target length
    let paddedInteger = integerPart;
    if (integerPart.length < targetLength) {
      paddedInteger = integerPart.padStart(targetLength, '0');
    }
    
    // Add commas to integer part
    const formattedInteger = paddedInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Return the formatted number with decimal part if needed
    return decimals > 0 ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <span
      ref={containerRef}
      onClick={restartAnimation} // Always allow manual restart on click
      style={{ userSelect: 'none' }}
      // className="tabular-nums"
      aria-live="polite"
      role="timer"
    >
      {formatValue(currentValue)}
    </span>
  );
}

// Smoother easing function - easeOutCubic
function easeOutCubic(x: number) {
  return 1 - (1 - x) ** 3;
}